<script setup>
import { ref, computed } from "vue";
import BillyBotAvatar from "./BillyBotAvatar.vue";
import {
  DocumentTextIcon,
  PencilIcon,
  PaperAirplaneIcon,
} from "@heroicons/vue/24/outline";
import CategoryIcon from "@/components/CategoryIcon.vue";
import BillyBotTextInput from "@/components/billybot/BillyBotTextInput.vue";
import SingleSelectInput from "./SingleSelectInput.vue";
import BillyBotFileUpload from "@/components/billybot/BillyBotFileUpload.vue";
import CancelEditConfirmation from "@/components/billybot/CancelEditConfirmation.vue";
import { useRequestSurveyStore } from "@/stores/requestSurvey";

const props = defineProps({
  category: {
    type: String,
    required: false,
    default: null,
    validator(value) {
      return [
        "car",
        "family",
        "finances",
        "home",
        "home / moving",
        "medical / employer benefits",
        "pet",
        "travel",
        "wildcard",
        "wildcard!",
      ].includes(value.toLowerCase());
    },
  },
  question: {
    type: Object,
    required: false,
    default: null,
  },
  summary: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(["response", "response:edit"]);

const requestSurveyStore = useRequestSurveyStore();
const initialValue = computed(() => {
  if (props.question.editing) {
    return requestSurveyStore.surveyEdits[props.question.id] || props.question.answer;
  } else {
    return props.question.answer || props.question.initialValue;
  }
});
const inputValue = ref(initialValue.value);
const fileUploadRef = ref();
const canContinue = computed(() => {
  if (props.question.type === "SingleUploadInput") {
    return fileUploadRef.value?.uploadRef?.files?.length > 0;
  }
  return !!inputValue.value;
});
const showCancelEditConfirmation = ref(false);

const emitResponse = (answer, customAnswerDisplay) => {
  delete requestSurveyStore.surveyEdits[props.question.id];

  const eventType = props.question.editing ? "response:edit" : "response";
  emit(eventType, {
    answer,
    customAnswerDisplay,
    questionId: props.question.id,
    finalQuestion: props.question.finalQuestion,
  });
};

const handleSubmit = async () => {
  let customAnswerDisplay;
  if (props.question.type === "SingleUploadInput") {
    inputValue.value = await fileUploadRef.value?.uploadFile();
    return;
  } else if (props.question.type === "SingleSelectInput") {
    const label = props.question.select_options?.find(
      (option) => option.value === inputValue.value,
    )?.label;
    customAnswerDisplay = {
      type: "single-select",
      label,
    };
  }
  emitResponse(inputValue.value, customAnswerDisplay);
};

const handleFileUploadSuccess = (file) => {
  const key = inputValue.value;
  const customAnswerDisplay = {
    type: "file",
    objectUrl: file.objectURL,
    fileName: file.name,
  };
  // Artificial 200ms delay to allow the upload complete status to be visible
  setTimeout(() => {
    emitResponse(key, customAnswerDisplay);
  }, 200);
};

const handleSkip = () => {
  const customAnswerDisplay = {
    type: "skip",
  };
  emitResponse(null, customAnswerDisplay);
};

const setEditing = () => {
  requestSurveyStore.surveyQuestions.find(
    (question) => question.id === props.question.id,
  ).editing = true;

  const previousEdit = requestSurveyStore.surveyEdits[props.question.id];
  inputValue.value = previousEdit || props.question.answer;
};

const handleCancelEditing = () => {
  if (inputValue.value !== props.question.answer) {
    showCancelEditConfirmation.value = true;
  } else {
    cancelEditing();
  }
};

const cancelEditing = () => {
  showCancelEditConfirmation.value = false;
  requestSurveyStore.surveyQuestions.find(
    (question) => question.id === props.question.id,
  ).editing = false;
  delete requestSurveyStore.surveyEdits[props.question.id];
};

const isEditable = computed(() => {
  return (
    props.question.answer !== undefined &&
    !props.question.editing &&
    !props.question.surveyComplete &&
    props.question.type !== "SingleUploadInput"
  );
});

const isAnswered = computed(() => {
  return props.question.answer || props.question.customAnswerDisplay;
});

const rowStyles = computed(() => ({
  base: [
    "relative m-4 flex flex-col items-start gap-x-4 gap-y-4 rounded-lg border border-brand-gray-50 p-6 sm:flex-row",
    {
      "bg-brand-primary-teal-highlight/60": !isAnswered.value || props.question.editing,
    },
  ],
  summary: "relative w-full",
}));
const handleInputChange = (event) => {
  requestSurveyStore.surveyEdits[props.question.id] = event.target.value;
};
</script>

<template>
  <div :class="summary ? rowStyles.summary : rowStyles.base">
    <CancelEditConfirmation
      v-if="showCancelEditConfirmation"
      @no="showCancelEditConfirmation = false"
      @yes="cancelEditing"
    />
    <template v-if="!summary">
      <BillyBotAvatar v-if="!isAnswered" />
      <CategoryIcon v-else :category="category" class="max-h-15 w-full max-w-15" />
    </template>
    <fieldset
      class="mb-4 w-full flex-1 text-black sm:mx-2 sm:w-auto"
      :inert="showCancelEditConfirmation"
    >
      <form @submit.prevent="handleSubmit">
        <button
          v-if="isEditable"
          :class="[
            'absolute top-5 cursor-pointer text-brand-primary-teal-400',
            summary ? 'right-0' : 'right-4',
          ]"
          @click="setEditing()"
        >
          <PencilIcon class="size-6" />
        </button>
        <legend
          :id="`question-label-${question.id}`"
          :class="['mb-4 sm:mr-4', { 'mr-10 font-bold': summary }]"
        >
          {{ question.label }}
        </legend>
        <div v-if="isAnswered && !question.editing" class="mr-10 flex-1" data-sr-mask>
          <div v-if="question.skipped">
            <span class="italic" data-sr-unmask>Question Skipped</span>
          </div>
          <div v-else-if="question.customAnswerDisplay">
            <p v-if="question.type === 'SingleSelectInput'">
              <span
                class="inline-block rounded-full border border-brand-primary-teal-400 bg-brand-primary-teal-400 px-5 py-3 font-bold text-white"
              >
                {{ question.customAnswerDisplay.label }}
              </span>
            </p>
            <div
              v-else-if="question.type === 'SingleUploadInput'"
              class="flex items-center gap-x-4"
            >
              <img
                v-if="question.customAnswerDisplay.objectUrl"
                :src="question.customAnswerDisplay.objectUrl"
                class="size-10"
                alt=""
              />
              <DocumentTextIcon v-else class="size-10 stroke-1" aria-hidden="true" />
              <StealieParagraph>
                {{ question.customAnswerDisplay.fileName }}
              </StealieParagraph>
            </div>
            <div v-else class="flex items-center">
              <DocumentTextIcon class="size-10" aria-hidden="true" />
              <StealieParagraph>
                {{ question.customAnswerDisplay.fileName }}
              </StealieParagraph>
            </div>
          </div>
          <p v-else>
            {{ question.answer }}
          </p>
        </div>
        <div v-else>
          <BillyBotTextInput
            v-if="
              [
                'TextInput',
                'PhoneNumberInput',
                'ManagedEmailInput',
                'MultilineTextInput',
              ].includes(question.type)
            "
            v-model="inputValue"
            :name="`text-input-${question.id}`"
            :label="question.label"
            :question-id="question.id"
            :initial-value="initialValue"
            :type="question.type"
            @change="handleInputChange"
          />
          <SingleSelectInput
            v-else-if="question.type === 'SingleSelectInput'"
            v-model="inputValue"
            :question-id="question.id"
            :selected-option="initialValue"
            :options="question.select_options"
            @change="handleInputChange"
          />
          <BillyBotFileUpload
            v-else-if="question.type === 'SingleUploadInput'"
            ref="fileUploadRef"
            @upload-success="handleFileUploadSuccess"
          />
          <div class="mt-4 -mr-4 flex justify-end divide-x divide-brand-gray-100 *:px-4">
            <button
              v-if="question.editing"
              class="flex cursor-pointer items-center gap-2 font-semibold text-brand-primary-teal-400 focus-visible:outline-focus-highlight-light active:text-brand-primary-teal-600 aria-disabled:cursor-not-allowed aria-disabled:text-brand-gray-100"
              type="button"
              aria-label="cancel and discard changes"
              @click="handleCancelEditing"
            >
              Cancel
            </button>
            <button
              v-if="question.optional"
              class="flex cursor-pointer items-center gap-2 font-semibold text-brand-primary-teal-400 focus-visible:outline-focus-highlight-light active:text-brand-primary-teal-600 aria-disabled:cursor-not-allowed aria-disabled:text-brand-gray-100"
              type="button"
              aria-label="skip this step"
              @click="handleSkip"
            >
              Skip
            </button>
            <button
              v-if="!question.editing"
              class="flex cursor-pointer items-center gap-2 font-semibold text-brand-primary-teal-400 transition-opacity delay-100 focus-visible:outline-focus-highlight-light active:text-brand-primary-teal-600 aria-disabled:cursor-not-allowed aria-disabled:text-brand-gray-100"
              type="submit"
              aria-label="Send"
              :disabled="!canContinue"
              :aria-disabled="!canContinue"
            >
              Continue
              <PaperAirplaneIcon
                class="pointer-events-none size-5 stroke-2 sm:size-4"
                aria-hidden="true"
              />
            </button>
            <button
              v-else
              class="flex cursor-pointer items-center gap-2 font-semibold text-brand-primary-teal-400 transition-opacity delay-100 focus-visible:outline-focus-highlight-light active:text-brand-primary-teal-600 aria-disabled:cursor-not-allowed aria-disabled:text-brand-gray-100"
              type="submit"
              aria-label="Send"
              :disabled="!canContinue"
              :aria-disabled="!canContinue"
            >
              Save
            </button>
          </div>
        </div>
      </form>
    </fieldset>
  </div>
</template>
